import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Avatar,
  Typography,
  Grid,
  Alert,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { PhotoCamera, Close, CloudUpload } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import useUserService from 'shared/api/services/userService';
import { uploadTenantUserPhoto } from 'shared/api/services/tenantUserService';
import toast from 'react-hot-toast';
import ClockLoader from 'shared/components/ClockLoader';

const validationSchema = Yup.object({
  file: Yup.mixed()
    .required('Please select an image file')
    .test('fileSize', 'File size must be less than 5MB', (value) => {
      return value && value.size <= 5 * 1024 * 1024;
    })
    .test('fileType', 'Only image files are allowed', (value) => {
      return value && value.type.startsWith('image/');
    }),
});

/**
 * @param {string} profileUserId — user UUID (profile being edited)
 * @param {string|null} [registryTenantId] — if set, upload uses tenant API + X-TenantID
 */
const EditPictureDialog = ({
  open,
  onClose,
  loadUserDetails,
  profileUserId,
  registryTenantId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const { uploadUserImage } = useUserService();

  const handleFileChange = useCallback((file, setFieldValue) => {
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setFieldValue('file', file);
    }
  }, []);

  const handleClose = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    onClose();
  }, [previewUrl, onClose]);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    if (!values.file) {
      setFieldError('file', 'Please select a file');
      return;
    }
    if (!profileUserId) {
      setFieldError('file', 'User id is missing');
      return;
    }

    setIsUploading(true);
    try {
      if (registryTenantId && String(registryTenantId).trim() !== '') {
        await uploadTenantUserPhoto(registryTenantId, profileUserId, values.file);
      } else {
        await uploadUserImage(values.file, profileUserId);
      }
      toast.success('Profile picture updated successfully!');
      loadUserDetails();
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to update profile picture');
    } finally {
      setIsUploading(false);
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="div">
          Upload Profile Image
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Formik
          initialValues={{ file: null }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center" mb={2}>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: previewUrl ? 'primary.main' : 'divider',
                        backgroundColor: isDark
                          ? 'background.paper'
                          : previewUrl
                            ? 'primary.50'
                            : 'grey.50',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {previewUrl ? (
                        <Avatar
                          src={previewUrl}
                          sx={{
                            width: 120,
                            height: 120,
                            border: '3px solid',
                            borderColor: 'primary.main',
                          }}
                        />
                      ) : (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          sx={{ width: 120, height: 120 }}
                        >
                          <PhotoCamera sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No image selected
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <input
                      accept="image/*"
                      type="file"
                      id="file-upload"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileChange(file, setFieldValue);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUpload />}
                        fullWidth
                        sx={{
                          py: 1.5,
                          borderStyle: 'dashed',
                          borderWidth: 2,
                          '&:hover': {
                            borderStyle: 'solid',
                            backgroundColor: isDark
                              ? alpha(theme.palette.primary.main, 0.12)
                              : 'primary.50',
                          },
                        }}
                      >
                        Choose Image
                      </Button>
                    </label>

                    {errors.file && touched.file && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {errors.file}
                      </Alert>
                    )}

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Supported formats: JPG, PNG, GIF. Max size: 5MB
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <DialogActions sx={{ pt: 2, gap: 1 }}>
                <Button onClick={handleClose} variant="outlined" disabled={isUploading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isUploading || !selectedFile}
                  startIcon={isUploading ? <ClockLoader inline size={16} /> : null}
                >
                  {isUploading ? 'Uploading...' : 'Upload Image'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditPictureDialog;
